"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ButtonAnimated from "@/components/ButtonAnimated/ButtonAnimated";
import "./Anmelden.css";
import successInput from "@/assets/anmelden/successInput.svg";
import Image from "next/image";
import errorInput from "@/assets/anmelden/errorInput.svg";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation";
import gmailIcon from "@/assets/anmelden/gmail.svg";
import aolIcon from "@/assets/anmelden/aol.svg";
import gmxIcon from "@/assets/anmelden/gmx.svg";
import icloudIcon from "@/assets/anmelden/icloud.svg";
import outlookIcon from "@/assets/anmelden/outlook.svg";
import protonIcon from "@/assets/anmelden/protonmail.svg";
import yahooIcon from "@/assets/anmelden/yahoo.svg";
import yandexIcon from "@/assets/anmelden/yandex.svg";
import zohoIcon from "@/assets/anmelden/zoho.svg";
import googleIcon from "@/assets/anmelden/google.svg";
import appleIcon from "@/assets/anmelden/apple.svg";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import {
  handleSignUpWithManual,
  handleSignInWithManual,
} from "@/app/helpers/userFunctions/handleSignIn";
import { useRouter, useSearchParams } from "next/navigation";
import { checkUserStatus } from "@/app/helpers/userFunctions/checkUserStatus";
import { auth } from "@/config/firebaseConfig";
import { sendLoginMail } from "../helpers/userFunctions/sendLoginMail";
import { trackPreReg, trackPreRegLink } from "@/app/helpers/tracking";
import {
  clearPreRegSession,
  getPreRegSessionId,
  getPreRegSourceFlow,
  setPreRegSourceFlow,
} from "@/app/helpers/preRegSession";
import useIsMobile from "../helpers/useIsMobile";
import { TopSectionLiveVerkauf } from "../[id]/sections/TopSection";
import SoldInfo from "./SoldInfo/SoldInfo";
import SoldInfoSolid from "./SoldInfo/SoldInfoSolid";
import { readAnmeldenFlowPayload } from "./anmeldenFlowPayload";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signOut,
} from "firebase/auth";
import trackVehicleCreated from "@/lib/trackVehicleCreated";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_TITLE = "Melde dich in deinem Autoeinfachlos-Konto an";
const APPLE_JS_API_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

function AnmeldenSection(props) {
  const {
    title: propTitle,
    fin: propFin,
    isManual: propIsManual,
    marke: propMarke,
    model: propModel,
    baujahr: propBaujahr,
    bodytype: propBodytype,
    fueltype: propFueltype,
    variant: propVariant,
    ps: propPs,
    gearbox: propGearbox,
    doors: propDoors,
    modelType: propModelType,
    kmStand: propKmStand,
    page: propPage,
  } = props ?? {};

  /* ----------------------------- state ----------------------------- */
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [navigationPending, setNavigationPending] = useState(false);
  const [socialAuthInProgress, setSocialAuthInProgress] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isFullNameValid, setIsFullNameValid] = useState(false);
  const [isFullNameTouched, setIsFullNameTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [step, setStep] = useState(1);
  const [noAccountFound, setNoAccountFound] = useState(false);
  const [flowPayload, setFlowPayload] = useState(null);
  const [isAppleDevice, setIsAppleDevice] = useState(false);
  const [hydrated, setHydrated] = useState(false); // ⇠ verhindert SSR-Mismatch
  const redirectToTargetTimerRef = useRef(null);
  const hardRedirectCheckTimerRef = useRef(null);
  const nameTrackedRef = useRef(false);
  const formViewedTrackedRef = useRef(false);
  const appleScriptLoadPromiseRef = useRef(null);
  const appleNonceRef = useRef(null);

  /* ----------------------------- misc hooks ----------------------------- */
  const api = useBackendUrl();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useIsMobile();
  const isFlowRequest =
    searchParams?.get("fromFlow") === "1" || searchParams?.get("flow") === "1";

  const clearRedirectTimers = useCallback(() => {
    if (typeof window === "undefined") return;
    if (redirectToTargetTimerRef.current !== null) {
      window.clearTimeout(redirectToTargetTimerRef.current);
      redirectToTargetTimerRef.current = null;
    }
    if (hardRedirectCheckTimerRef.current !== null) {
      window.clearTimeout(hardRedirectCheckTimerRef.current);
      hardRedirectCheckTimerRef.current = null;
    }
  }, []);

  /* mark hydration complete */
  useEffect(() => setHydrated(true), []);

  useEffect(() => {
    router.prefetch("/account");
  }, [router]);

  useEffect(() => {
    if (!isFlowRequest) {
      setFlowPayload(null);
      return;
    }

    const payload = readAnmeldenFlowPayload();
    setFlowPayload(payload && typeof payload === "object" ? payload : null);
  }, [isFlowRequest]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ua = window.navigator.userAgent || "";
    const platform = window.navigator.platform || "";
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;
    const isAppleUa = /iPad|iPhone|iPod|Macintosh/i.test(ua);
    const isIPadOs = platform === "MacIntel" && maxTouchPoints > 1;
    setIsAppleDevice(isAppleUa || isIPadOs);
  }, []);

  const activeFlowPayload = useMemo(() => {
    if (!isFlowRequest) return null;
    if (!flowPayload || typeof flowPayload !== "object") return null;
    return flowPayload;
  }, [isFlowRequest, flowPayload]);

  const title = propTitle ?? activeFlowPayload?.title ?? DEFAULT_TITLE;
  const fin = propFin ?? activeFlowPayload?.fin ?? null;
  const isManual = propIsManual ?? activeFlowPayload?.isManual ?? false;
  const marke = propMarke ?? activeFlowPayload?.marke;
  const model = propModel ?? activeFlowPayload?.model;
  const baujahr = propBaujahr ?? activeFlowPayload?.baujahr;
  const bodytype = propBodytype ?? activeFlowPayload?.bodytype;
  const fueltype = propFueltype ?? activeFlowPayload?.fueltype;
  const variant = propVariant ?? activeFlowPayload?.variant;
  const ps = propPs ?? activeFlowPayload?.ps;
  const gearbox = propGearbox ?? activeFlowPayload?.gearbox;
  const doors = propDoors ?? activeFlowPayload?.doors;
  const modelType = propModelType ?? activeFlowPayload?.modelType;
  const kmStand = propKmStand ?? activeFlowPayload?.kmStand;
  const page = propPage ?? activeFlowPayload?.page;

  useEffect(() => {
    return () => {
      clearRedirectTimers();
    };
  }, [clearRedirectTimers]);

  /* ---------------------- build carData sync ---------------------- */
  const carData = useMemo(() => {
    if (
      !marke ||
      !model ||
      !baujahr ||
      !bodytype ||
      !fueltype ||
      !variant ||
      !ps ||
      !gearbox ||
      !doors ||
      !modelType ||
      !kmStand
    ) {
      return null;
    }

    const toName = (v) => (v && typeof v === "object" ? v.name : v);
    const modelName = `${toName(model)} ${toName(variant)} ${toName(modelType)}`;

    return {
      marke: toName(marke),
      model: modelName,
      baujahr: toName(baujahr),
      vehicleFeatures: { bodytype: toName(bodytype) },
      fueltype: toName(fueltype),
      variant: toName(variant),
      ps: toName(ps),
      gearbox: toName(gearbox),
      doors: toName(doors),
      modelType: toName(modelType),
      kmStand,
    };
  }, [
    marke,
    model,
    baujahr,
    bodytype,
    fueltype,
    variant,
    ps,
    gearbox,
    doors,
    modelType,
    kmStand,
  ]);

  /* ---------------------------- handlers ----------------------------- */
  const handleEmailChange = (e) => {
    const input = e.target.value;
    setEmail(input);
    setIsEmailValid(EMAIL_REGEX.test(input));
  };

  const handleLogin = async () => {
    const ok = await sendLoginMail(email, fin, kmStand, api);
    setEmailSent(ok);
    setNoAccountFound(!ok);
  };

  const redirectToHome = () => router.push("/");

  const handleCheckUser = async () => {
    setLoadingFetch(true);
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" }),
    );
    try {
      const userStatus = await checkUserStatus(
        email,
        fin,
        kmStand,
        api,
        isManual,
      );
      // Determine sourceFlow if not already set (e.g. from Homepage step tracking)
      if (!getPreRegSourceFlow()) {
        if (userStatus === "signUpWithFin") {
          setPreRegSourceFlow("fin_flow");
        } else if (isFlowRequest) {
          // Derive from flow payload page (Homepage, /marken/..., /auto-verkaufen, etc.)
          const pg = activeFlowPayload?.page;
          if (pg === "Homepage") setPreRegSourceFlow("homepage");
          else if (typeof pg === "string" && pg.startsWith("/marken")) setPreRegSourceFlow("marken");
          else if (typeof pg === "string" && pg.startsWith("/auto-verkaufen")) setPreRegSourceFlow("auto_verkaufen");
          else if (typeof pg === "string" && pg.startsWith("/auto-bewerten")) setPreRegSourceFlow("auto_bewerten");
          else setPreRegSourceFlow("flow");
        } else {
          setPreRegSourceFlow("anmelden_direct");
        }
      }
      trackPreReg(api, getPreRegSessionId(), "auth_email");
      if (isManual) {
        if (userStatus === "manualUserExists") await handleSignInManual();
        else if (userStatus === "manualUserNotFound") {
          setNoAccountFound(false);
          setStep(2);
        } else alert("Etwas ist schiefgelaufen");
      } else {
        if (userStatus === "existingUser") {
          await handleLogin();
          return;
        }
        else if (userStatus === "signUpWithFin") {
          setNoAccountFound(false);
          setStep(2);
        } else if (userStatus === "noAccountFound") setNoAccountFound(true);
        else alert("Etwas ist schiefgelaufen");
      }
    } catch (err) {
      console.error(err);
      alert("Ein Fehler ist aufgetreten. Bitte später erneut versuchen.");
    } finally {
      setLoadingFetch(false);
    }
  };

  const submitSignUpAndNavigate = async ({
    emailValue,
    firstNameValue,
    lastNameValue,
    telefonE164,
  }) => {
    // Pre-reg tracking: user clicked submit (before API call)
    trackPreReg(api, getPreRegSessionId(), "submit_attempted");

    setLoadingFetch(true);
    setNavigationPending(false);
    let shouldResetLoading = true;

    try {
      const {
        success,
        vehicleId,
        price,
        priceCalculationAutomated,
        signedInUid,
        error,
      } =
        await handleSignUpWithManual(
          emailValue,
          firstNameValue,
          lastNameValue,
          telefonE164,
          kmStand,
          marke,
          model,
          gearbox,
          fueltype,
          bodytype,
          baujahr,
          doors,
          ps,
          variant,
          modelType,
          api,
          page,
          fin,
          // router hier NICHT mehr übergeben
        );

      if (success && vehicleId) {
        // Pre-reg tracking: signup succeeded
        trackPreReg(api, getPreRegSessionId(), "registration_succeeded");
        trackPreRegLink(api, getPreRegSessionId(), vehicleId);
        clearPreRegSession();

        const waitForAuthReady = (expectedUid, timeoutMs = 8000) =>
          new Promise((resolve) => {
            let done = false;
            const timer = setTimeout(() => {
              if (done) return;
              done = true;
              unsub();
              resolve(false);
            }, timeoutMs);
            const isExpectedUser = (user) => {
              if (!user) return false;
              if (!expectedUid) return true;
              return user.uid === expectedUid;
            };

            if (isExpectedUser(auth.currentUser)) {
              done = true;
              clearTimeout(timer);
              resolve(true);
              return;
            }

            const unsub = auth.onAuthStateChanged((user) => {
              if (done) return;
              if (isExpectedUser(user)) {
                done = true;
                clearTimeout(timer);
                unsub();
                resolve(true);
              }
            });
          });

        const authReady = await waitForAuthReady(signedInUid);
        if (!authReady) {
          alert("Login fehlgeschlagen. Bitte erneut versuchen.");
          return false;
        }

        try {
          await establishFrontendSessionCookie();
        } catch (sessionErr) {
          console.error(
            "[AnmeldenSection] Failed to establish frontend session cookie after manual signup:",
            sessionErr,
          );
          alert("Login konnte nicht abgeschlossen werden. Bitte erneut versuchen.");
          return false;
        }

        // Zielroute hier bauen
        trackVehicleCreated({ vehicleId, entrypoint: "anmelden", sourceFlow: getPreRegSourceFlow() || "anmelden_direct" });

        // Automatisierte Fahrzeuge gehen zuerst auf /status (löst die externe
        // Bewertung aus → Preis oder manueller Flow). Wie alle anderen
        // Erstellungs-Einstiege; sonst würde dieser Signup-Pfad nie bepreisen.
        const targetRoute = priceCalculationAutomated
          ? `/status/${vehicleId}`
          : price !== null
            ? `/preis/${vehicleId}`
            : `/status/${vehicleId}`;

        shouldResetLoading = false;
        navigateToTargetRoute(targetRoute);
        return true; // Component wird eh unmounted, kein setLoadingFetch nötig
      }

      console.error("[AnmeldenSection] SignUp failed:", error);
      alert("Fehler: Bitte Eingaben prüfen oder später erneut versuchen.");
      return false;
    } catch (err) {
      console.error("[AnmeldenSection] SignUp exception:", err);
      alert("Ein Fehler ist aufgetreten. Bitte später erneut versuchen.");
      return false;
    } finally {
      if (shouldResetLoading) {
        setLoadingFetch(false);
        setNavigationPending(false);
      }
    }
  };

  const handleSignUpManual = async () => {
    if (loadingFetch) return; // gegen Doppelklick absichern

    await submitSignUpAndNavigate({
      emailValue: email,
      firstNameValue: vorname,
      lastNameValue: nachname,
      telefonE164: null,
    });
  };

  const handleSignInManual = async () => {
    setLoadingFetch(true);
    const success = await handleSignInWithManual(
      email,
      kmStand,
      marke,
      model,
      gearbox,
      fueltype,
      bodytype,
      baujahr,
      doors,
      ps,
      variant,
      modelType,
      api,
      page,
      fin,
    );
    if (success) setEmailSent(true);
    else if (success === false) alert("Fehler: Bitte Eingaben prüfen.");
    else alert("Serverproblem. Bitte später versuchen.");
    setLoadingFetch(false);
  };

  const splitName = useCallback((displayName) => {
    const safe = String(displayName || "").trim();
    if (!safe) return { firstName: "", lastName: "", fullName: "" };
    const parts = safe.split(/\s+/).filter(Boolean);
    if (parts.length < 2) {
      return { firstName: parts[0] || "", lastName: "", fullName: safe };
    }
    const lastName = parts.pop() || "";
    const firstName = parts.join(" ");
    return { firstName, lastName, fullName: safe };
  }, []);

  const applyGoogleProfileToForm = useCallback((user) => {
    const providerEmail = String(user?.email || "").trim().toLowerCase();
    const { firstName, lastName, fullName } = splitName(user?.displayName);

    if (providerEmail) {
      setEmail(providerEmail);
      setIsEmailValid(EMAIL_REGEX.test(providerEmail));
    }
    if (fullName) setFullName(fullName);
    if (firstName) setVorname(firstName);
    if (lastName) setNachname(lastName);
    if (firstName && lastName) {
      setIsFullNameValid(true);
      setIsFullNameTouched(true);
    }
  }, [splitName]);

  const establishFrontendSessionCookie = useCallback(async () => {
    const idToken = await auth.currentUser?.getIdToken(true);
    if (!idToken) throw new Error("missing idToken");

    const res = await fetch(`${api}/login/session-cookie`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ idToken }),
    });

    if (!res.ok) {
      throw new Error(`session-cookie failed: ${res.status}`);
    }

    const data = await res.json();
    const sessionCookie = data?.sessionCookie;
    if (!sessionCookie) throw new Error("missing sessionCookie");
    const sessionEmail = String(auth.currentUser?.email || "")
      .trim()
      .toLowerCase();

    const prod = process.env.NODE_ENV === "production";
    document.cookie = `__session_front=${sessionCookie}; Path=/; SameSite=Lax; Max-Age=${
      14 * 24 * 60 * 60
    }${prod ? "; Secure" : ""}`;
    if (sessionEmail) {
      document.cookie = `__session_front_email=${encodeURIComponent(
        sessionEmail,
      )}; Path=/; SameSite=Lax; Max-Age=${14 * 24 * 60 * 60}${
        prod ? "; Secure" : ""
      }`;
    }
  }, [api]);

  const navigateToTargetRoute = useCallback((targetRoute) => {
    setNavigationPending(true);
    setLoadingFetch(true);

    if (typeof window === "undefined") {
      router.replace(targetRoute);
      return;
    }

    const isStillOnAnmelden = () => {
      const path = String(window.location.pathname || "");
      return path === "/anmelden" || path === "/anmelden/";
    };

    clearRedirectTimers();
    router.replace(targetRoute);

    redirectToTargetTimerRef.current = window.setTimeout(() => {
      if (isStillOnAnmelden()) {
        // Fallback to hard redirect if client-side navigation is delayed/stuck.
        window.location.replace(targetRoute);
        hardRedirectCheckTimerRef.current = window.setTimeout(() => {
          if (isStillOnAnmelden()) {
            console.error(
              "[AnmeldenSection] Redirect to target route failed",
              targetRoute,
            );
            setLoadingFetch(false);
            setNavigationPending(false);
            alert("Weiterleitung fehlgeschlagen. Bitte versuche es erneut.");
          }
          hardRedirectCheckTimerRef.current = null;
        }, 10000);
      }
      redirectToTargetTimerRef.current = null;
    }, 1200);
  }, [clearRedirectTimers, router]);

  const navigateAfterSocialAuth = useCallback((target) => {
    navigateToTargetRoute(target);
  }, [navigateToTargetRoute]);

  const clearFrontendSessionCookie = useCallback(() => {
    const prod = process.env.NODE_ENV === "production";
    document.cookie = `__session_front=; Path=/; SameSite=Lax; Max-Age=0${
      prod ? "; Secure" : ""
    }`;
    document.cookie = `__session_front_email=; Path=/; SameSite=Lax; Max-Age=0${
      prod ? "; Secure" : ""
    }`;
  }, []);

  const addManualVehicleForSignedInUser = useCallback(async (googleEmail) => {
    const toName = (v) => (v && typeof v === "object" ? v.name : v);
    const toId = (v) => (v && typeof v === "object" ? v.id : null);

    const payload = {
      email: googleEmail,
      kmStand,
      marke: toName(marke),
      model: toName(model),
      gearbox: toName(gearbox),
      fueltype: toName(fueltype),
      bodytype: toName(bodytype),
      baujahr: toName(baujahr),
      doors: toId(doors) ?? toName(doors),
      ps: toName(ps),
      variant: toName(variant) ?? "",
      type: toName(modelType) ?? "",
      vehicleDataIds: {
        brand: toId(marke),
        model: toId(model),
        year: toId(baujahr),
        body: toId(bodytype),
        fuel: toId(fueltype),
        variant: toId(variant) ?? toName(variant) ?? null,
        kw: toId(ps),
        gearbox: toId(gearbox),
        doors: toId(doors),
        modelType: toId(modelType),
      },
      salesdate: "2025-06-01",
    };

    const backendRes = await fetch(`${api}/user/add-vehicle-manually`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const json = await backendRes.json();
    if (!backendRes.ok) {
      throw new Error(json?.error || "manual add failed");
    }

    const priceCalculationAutomated =
      json?.ab?.priceCalculationAutomated === true ||
      json?.priceCalculationAutomated === true;
    return priceCalculationAutomated
      ? `/status/${json.id}`
      : json?.vehicleMatchFound
        ? `/preis/${json.id}`
        : `/status/${json.id}`;
  }, [
    api,
    baujahr,
    bodytype,
    doors,
    fueltype,
    gearbox,
    kmStand,
    marke,
    model,
    modelType,
    ps,
    variant,
  ]);

  const loadAppleJsApi = useCallback(() => {
    if (typeof window === "undefined") {
      return Promise.reject(new Error("Apple JS API requires browser"));
    }
    if (window.AppleID?.auth?.signIn) {
      return Promise.resolve(window.AppleID);
    }
    if (appleScriptLoadPromiseRef.current) {
      return appleScriptLoadPromiseRef.current;
    }

    appleScriptLoadPromiseRef.current = new Promise((resolve, reject) => {
      const onReady = () => {
        if (window.AppleID?.auth?.signIn) {
          resolve(window.AppleID);
          return;
        }
        reject(new Error("Apple JS API did not initialize"));
      };

      const existingScript = document.querySelector(
        `script[src=\"${APPLE_JS_API_SRC}\"]`,
      );
      if (existingScript) {
        if (existingScript.getAttribute("data-loaded") === "true") {
          onReady();
          return;
        }
        existingScript.addEventListener("load", onReady, { once: true });
        existingScript.addEventListener(
          "error",
          () => reject(new Error("Failed to load Apple JS API")),
          { once: true },
        );
        return;
      }

      const script = document.createElement("script");
      script.src = APPLE_JS_API_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        script.setAttribute("data-loaded", "true");
        onReady();
      };
      script.onerror = () => reject(new Error("Failed to load Apple JS API"));
      document.head.appendChild(script);
    });

    return appleScriptLoadPromiseRef.current;
  }, []);

  const createNonce = useCallback((bytesLength = 16) => {
    const bytes = new Uint8Array(bytesLength);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }, []);

  const shouldShowDetailedAuthAlert =
    String(process.env.NEXT_PUBLIC_ENV || "").toUpperCase() !== "MAIN";

  const sha256Hex = useCallback(async (value) => {
    const enc = new TextEncoder();
    const data = enc.encode(value);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }, []);

  const primeAppleNonce = useCallback(async () => {
    const rawNonce = createNonce(16);
    const hashedNonce = await sha256Hex(rawNonce);
    appleNonceRef.current = { rawNonce, hashedNonce };
  }, [createNonce, sha256Hex]);

  const handleSocialUserAuthenticated = async (firebaseUser) => {
    const providerEmail = String(firebaseUser?.email || "")
      .trim()
      .toLowerCase();
    if (!providerEmail) {
      throw new Error("Social account has no email");
    }

    applyGoogleProfileToForm(firebaseUser);
    await establishFrontendSessionCookie();

    const userStatus = await checkUserStatus(
      providerEmail,
      fin,
      kmStand,
      api,
      isManual,
    );

    if (isManual) {
      if (userStatus === "manualUserExists") {
        try {
          const targetRoute = await addManualVehicleForSignedInUser(providerEmail);
          navigateAfterSocialAuth(targetRoute);
          return true;
        } catch (err) {
          const message = String(err?.message || "");
          if (message === "vehicle-limit-reached") {
            // Keep user signed in, but skip vehicle creation when limit is reached.
            navigateAfterSocialAuth("/account");
            return true;
          }
          throw err;
        }
      }

      if (userStatus === "manualUserNotFound") {
        setNoAccountFound(false);
        setStep(2);
        return false;
      }

      throw new Error("Unexpected manual user status");
    }

    if (userStatus === "existingUser") {
      const targetRoute = fin ? `/${encodeURIComponent(fin)}/profil` : "/account";
      navigateAfterSocialAuth(targetRoute);
      return true;
    }

    if (userStatus === "signUpWithFin") {
      setNoAccountFound(false);
      setStep(2);
      return false;
    }

    if (userStatus === "noAccountFound") {
      setNoAccountFound(true);
      clearFrontendSessionCookie();
      await signOut(auth).catch(() => {});
      return false;
    }

    throw new Error("Unexpected user status");
  };

  useEffect(() => {
    loadAppleJsApi().catch(() => {});
    primeAppleNonce().catch(() => {});
  }, [loadAppleJsApi, primeAppleNonce]);

  const runSocialLogin = async (provider, providerLabel) => {
    if (loadingFetch || socialAuthInProgress) return;
    setSocialAuthInProgress(true);
    let shouldResetLoading = true;

    try {
      const result = await signInWithPopup(auth, provider);
      // Show full-page loader only after popup is approved and closed.
      setLoadingFetch(true);
      const navigated = await handleSocialUserAuthenticated(result.user);
      shouldResetLoading = !navigated;
    } catch (err) {
      const code = String(err?.code || "");
      if (
        code === "auth/popup-blocked" ||
        code === "auth/operation-not-supported-in-this-environment"
      ) {
        alert(
          `${providerLabel}-Anmeldung benötigt ein Popup. Bitte Popups erlauben und erneut versuchen.`,
        );
        return;
      }
      if (code === "auth/popup-closed-by-user") {
        return;
      }
      console.error(`[AnmeldenSection] ${providerLabel} login error:`, err);
      alert(`${providerLabel}-Anmeldung fehlgeschlagen. Bitte erneut versuchen.`);
    } finally {
      setSocialAuthInProgress(false);
      if (shouldResetLoading) {
        setLoadingFetch(false);
        setNavigationPending(false);
      }
    }
  };

  const handleGoogleLoginClick = async () => {
    trackPreReg(api, getPreRegSessionId(), "auth_google");
    const provider = new GoogleAuthProvider();
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const customParams = {
      include_granted_scopes: "true",
    };

    if (EMAIL_REGEX.test(normalizedEmail)) {
      customParams.login_hint = normalizedEmail;
    }

    provider.setCustomParameters(customParams);
    await runSocialLogin(provider, "Google");
  };

  const handleAppleLoginClick = async () => {
    if (loadingFetch || socialAuthInProgress) return;
    trackPreReg(api, getPreRegSessionId(), "auth_apple");
    const clientId = process.env.NEXT_PUBLIC_APPLE_SERVICE_ID;
    const configuredRedirectUri = process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI;
    const redirectURI =
      configuredRedirectUri || `${window.location.origin}/anmelden`;

    if (!clientId) {
      alert(
        "Apple-Anmeldung ist nicht vollständig konfiguriert (NEXT_PUBLIC_APPLE_SERVICE_ID fehlt).",
      );
      return;
    }

    setSocialAuthInProgress(true);
    let shouldResetLoading = true;

    try {
      if (!window.AppleID?.auth?.signIn) {
        loadAppleJsApi().catch(() => {});
        throw new Error("apple_js_not_ready");
      }
      if (!appleNonceRef.current) {
        primeAppleNonce().catch(() => {});
        throw new Error("apple_nonce_not_ready");
      }

      const state = createNonce(16);
      const { rawNonce, hashedNonce } = appleNonceRef.current;
      appleNonceRef.current = null;
      primeAppleNonce().catch(() => {});

      window.AppleID.auth.init({
        clientId,
        scope: "name email",
        redirectURI,
        state,
        nonce: hashedNonce,
        usePopup: true,
      });

      const appleResult = await window.AppleID.auth.signIn();
      const idToken = appleResult?.authorization?.id_token;
      if (!idToken) {
        throw new Error("missing Apple id_token");
      }

      const provider = new OAuthProvider("apple.com");
      const credential = provider.credential({
        idToken,
        rawNonce,
      });
      const userCredential = await signInWithCredential(auth, credential);

      setLoadingFetch(true);
      const navigated = await handleSocialUserAuthenticated(userCredential.user);
      shouldResetLoading = !navigated;
    } catch (err) {
      const code = String(err?.code || "");
      const appleError = String(err?.error || "");
      const message = String(err?.message || "");
      const errorDescription = String(err?.error_description || "");
      if (message === "apple_js_not_ready" || message === "apple_nonce_not_ready") {
        alert("Apple-Anmeldung wird vorbereitet. Bitte in 1 Sekunde erneut tippen.");
        return;
      }
      if (
        code === "auth/popup-closed-by-user" ||
        appleError === "popup_closed_by_user"
      ) {
        return;
      }
      if (appleError === "popup_blocked_by_browser") {
        alert(
          "Apple-Popup wurde vom Browser blockiert. Bitte Popups für diese Seite erlauben und erneut versuchen.",
        );
        return;
      }
      console.error("[AnmeldenSection] Apple login error:", {
        code,
        appleError,
        errorDescription,
        message,
        err,
      });
      if (shouldShowDetailedAuthAlert) {
        const detailLines = [
          code ? `code: ${code}` : null,
          appleError ? `apple_error: ${appleError}` : null,
          errorDescription ? `apple_description: ${errorDescription}` : null,
          message ? `message: ${message}` : null,
        ].filter(Boolean);
        alert(
          `Apple-Anmeldung fehlgeschlagen.\n${detailLines.join("\n") || "no error details"}`,
        );
      } else {
        alert("Etwas ist schiefgelaufen, bitte versuche erneut.");
      }
    } finally {
      setSocialAuthInProgress(false);
      if (shouldResetLoading) {
        setLoadingFetch(false);
        setNavigationPending(false);
      }
    }
  };

  const getMailInfo = (mail) => {
    const domain = mail.split("@")[1];
    const providers = {
      "gmail.com": {
        link: "https://mail.google.com/mail/u/0/#inbox",
        image: gmailIcon,
      },
      "yahoo.com": {
        link: "https://mail.yahoo.com/d/folders/1",
        image: yahooIcon,
      },
      "outlook.com": {
        link: "https://outlook.live.com/mail/inbox",
        image: outlookIcon,
      },
      "icloud.com": { link: "https://www.icloud.com/mail", image: icloudIcon },
      "protonmail.com": {
        link: "https://mail.protonmail.com/",
        image: protonIcon,
      },
      "proton.me": { link: "https://mail.protonmail.com/", image: protonIcon },
      "zoho.com": { link: "https://mail.zoho.com/", image: zohoIcon },
      "gmx.com": { link: "https://mail.gmx.com/", image: gmxIcon },
      "aol.com": { link: "https://mail.aol.com/", image: aolIcon },
      "yandex.com": { link: "https://mail.yandex.com/", image: yandexIcon },
    };
    return providers[domain] || null;
  };

  const handleFullNameChange = (e) => {
    const val = e.target.value;
    setFullName(val);

    const parts = val.trim().split(/\s+/).filter(Boolean);

    const countLetters = (s) => (s.match(/\p{L}/gu) || []).length; // Unicode letters (ä, ö, ü, ğ, ş, …)

    // nichts eingegeben
    if (parts.length === 0) {
      setVorname("");
      setNachname("");
      setIsFullNameValid(false);
      return;
    }

    // nur ein Wort → ungültig (Eren ❌)
    if (parts.length === 1) {
      setVorname(parts[0]);
      setNachname("");
      setIsFullNameValid(false);
      return;
    }

    // zwei oder mehr Wörter
    const lastName = parts.pop() || "";
    const firstName = parts.join(" ");

    setVorname(firstName);
    setNachname(lastName);

    // ✅ Regel:
    // - Vorname: mind. 2 Buchstaben
    // - Nachname: mind. 1 Buchstabe (Eren B ✅)
    const valid = countLetters(firstName) >= 2 && countLetters(lastName) >= 1;
    setIsFullNameValid(valid);
  };

  const showTopSection = Boolean(carData);
  const showSoldInfo = step === 1 && !emailSent && !noAccountFound;
  const showSoldInfoSolid = step === 2 && !emailSent;
  const showLoadingScreen = loadingFetch || navigationPending;

  // Track "registration_form_viewed" when Step 1 (email + social buttons) is shown
  useEffect(() => {
    if (showSoldInfo && !formViewedTrackedRef.current) {
      formViewedTrackedRef.current = true;
      trackPreReg(api, getPreRegSessionId(), "registration_form_viewed");
    }
  }, [showSoldInfo, api]);

  /* ---------------------------- render ----------------------------- */
  return (
    <div className="Anmelden-wrapper">
      {showTopSection && (
        <div className="Anmelden-top-enter">
          <TopSectionLiveVerkauf carData={carData} />
        </div>
      )}

      {showLoadingScreen ? (
        <div className="Anmelden-loading-block">
          <LoadingAnimation
            style={{
              height: "100%",
              minHeight: "100%",
              maxHeight: "100%",
            }}
          />
        </div>
      ) : (
        <>
          {/* SoldInfo stays visible only on regular step screens */}
          {showSoldInfo &&
            (hydrated && isMobile ? (
              <SoldInfo brand={carData?.marke} />
            ) : (
              <SoldInfo brand={carData?.marke} />
            ))}

          {showSoldInfoSolid &&
            (hydrated && isMobile ? (
              <SoldInfoSolid brand={carData?.marke} />
            ) : (
              <SoldInfo brand={carData?.marke} />
            ))}

          {/* ------------- Kein Account ------------- */}
          {noAccountFound && (
            <div className="Anmelden-container">
              <h2 className="Anmelden-h2">
                Kein Account mit dieser Email-Adresse
              </h2>
              <p className="Anmelden-subtitle">
                Bitte überprüfe deine E-Mail-Adresse oder gib deine
                Fahrzeugdaten
                <br />
                auf der Startseite ein, um ein Konto zu erstellen.
              </p>
              <div className="Anmelden-input-wrapper">
                <ButtonAnimated
                  text="Zurück"
                  hideMaxWidth
                  width="100%"
                  height="48px"
                  fontSize="18px"
                  onClick={redirectToHome}
                  disabled={loadingFetch}
                  className="Anmelden-button"
                />
              </div>
            </div>
          )}

          {/* ------------- Step 1 ------------- */}
          {step === 1 && !emailSent && !noAccountFound && (
            <>
              <div className="Anmelden-container">
                <h1 className="Anmelden-title">{title}</h1>
                <div className="Anmelden-input-wrapper">
                  <p>E-Mail-Adresse</p>
                  <div className="Anmelden-input-container">
                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      className={`Anmelden-input ${
                        isEmailValid && !isFocused
                          ? "valid"
                          : email && !isFocused
                            ? "invalid"
                            : ""
                      }`}
                      placeholder="Bsp. johnsmith@gmail.com"
                    />
                    {!isFocused && !isEmailValid && email.length > 1 && (
                      <Image
                        src={errorInput}
                        alt="Error"
                        className="Anmelden-input-icon"
                      />
                    )}
                    {!isFocused && isEmailValid && (
                      <Image
                        src={successInput}
                        alt="Success"
                        className="Anmelden-input-icon"
                      />
                    )}
                  </div>
                  <ButtonAnimated
                    text="Weiter"
                    hideMaxWidth
                    width="100%"
                    height="48px"
                    fontSize="18px"
                    onClick={handleCheckUser}
                    disabled={!isEmailValid || loadingFetch}
                    className="Anmelden-button"
                  />
                  {/*
                  <div className="Anmelden-auth-divider" role="separator">
                    <span>oder</span>
                  </div>
                  <div className="Anmelden-social-buttons">
                    <button
                      type="button"
                      className="Anmelden-social-button"
                      onClick={handleGoogleLoginClick}
                      disabled={loadingFetch || socialAuthInProgress}
                    >
                      <Image
                        src={googleIcon}
                        alt=""
                        aria-hidden="true"
                        className="Anmelden-social-icon"
                      />
                      <span>Mit Google fortfahren</span>
                    </button>
                    {isAppleDevice && (
                      <button
                        type="button"
                        className="Anmelden-social-button"
                        onClick={handleAppleLoginClick}
                        disabled={loadingFetch || socialAuthInProgress}
                      >
                        <Image
                          src={appleIcon}
                          alt=""
                          aria-hidden="true"
                          className="Anmelden-social-icon"
                        />
                        <span>Mit Apple fortfahren</span>
                      </button>
                    )}
                  </div>
                  */}
                </div>
              </div>
            </>
          )}

          {/* ------------- Step 2: Registrierung ------------- */}
          {step === 2 && !emailSent && (
            <>
              <div className="Anmelden-container">
                <h2 className="Anmelden-h2">Wir können dein Auto verkaufen!</h2>
                <p className="Anmelden-subtitle">
                  Gib deine Daten ein, um eine sofortige Bewertung zu erhalten.
                </p>

                <div className="Anmelden-used-before-box">
                  <p>Autoeinfachlos schonmal benutzt?</p>
                  <p
                    className="Anmelden-used-before-box-link transition"
                    onClick={() => setStep(1)}
                  >
                    Login
                  </p>
                </div>

                <div className="Anmelden-input-wrapper">
                  <p>Vollständiger Name</p>
                  <div className="Anmelden-input-container">
                    <input
                      type="text"
                      value={fullName}
                      onChange={handleFullNameChange}
                      onBlur={() => {
                        setIsFullNameTouched(true);
                        if (!nameTrackedRef.current && isFullNameValid) {
                          nameTrackedRef.current = true;
                          trackPreReg(api, getPreRegSessionId(), "name_entered");
                        }
                      }}
                      onFocus={() => setIsFullNameTouched(false)}
                      placeholder="Max Mustermann"
                      className={`Anmelden-input ${
                        isFullNameTouched
                          ? isFullNameValid
                            ? "valid"
                            : "invalid"
                          : ""
                      }`}
                    />
                    {isFullNameTouched &&
                      !isFullNameValid &&
                      fullName.length > 1 && (
                        <Image
                          src={errorInput}
                          alt="Error"
                          className="Anmelden-input-icon"
                        />
                      )}
                    {isFullNameTouched && isFullNameValid && (
                      <Image
                        src={successInput}
                        alt="Success"
                        className="Anmelden-input-icon"
                      />
                    )}
                  </div>

                  <p>E-Mail</p>
                  <div className="Anmelden-input-container">
                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      className={`Anmelden-input ${
                        isEmailValid && !isFocused
                          ? "valid"
                          : email && !isFocused
                            ? "invalid"
                            : ""
                      }`}
                      placeholder="maxmustermann@gmail.com"
                    />
                    {!isFocused && !isEmailValid && email.length > 1 && (
                      <Image
                        src={errorInput}
                        alt="Error"
                        className="Anmelden-input-icon"
                      />
                    )}
                    {!isFocused && isEmailValid && (
                      <Image
                        src={successInput}
                        alt="Success"
                        className="Anmelden-input-icon"
                      />
                    )}
                  </div>

                  <ButtonAnimated
                    text="Fortfahren"
                    onClick={handleSignUpManual}
                    hideMaxWidth
                    width="100%"
                    height="48px"
                    fontSize="18px"
                    disabled={
                      loadingFetch ||
                      !email ||
                      !vorname ||
                      !nachname ||
                      !kmStand
                    }
                    className="Anmelden-button"
                  />

                  <div className="Anmelden-trust">
                    <div className="Anmelden-trust-item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1L2 4V7.33C2 10.84 4.56 14.11 8 15C11.44 14.11 14 10.84 14 7.33V4L8 1Z" fill="#6b7280" fillOpacity="0.15" stroke="#6b7280" strokeWidth="1.2"/>
                        <path d="M6 8L7.5 9.5L10.5 6.5" stroke="#6b7280" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>DSGVO-konform</span>
                    </div>
                    <div className="Anmelden-trust-item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1L2 4V7.33C2 10.84 4.56 14.11 8 15C11.44 14.11 14 10.84 14 7.33V4L8 1Z" fill="#6b7280" fillOpacity="0.15" stroke="#6b7280" strokeWidth="1.2"/>
                        <path d="M6 8L7.5 9.5L10.5 6.5" stroke="#6b7280" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Kein Spam</span>
                    </div>
                    <div className="Anmelden-trust-item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 1L2 4V7.33C2 10.84 4.56 14.11 8 15C11.44 14.11 14 10.84 14 7.33V4L8 1Z" fill="#6b7280" fillOpacity="0.15" stroke="#6b7280" strokeWidth="1.2"/>
                        <path d="M6 8L7.5 9.5L10.5 6.5" stroke="#6b7280" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Kostenlos & unverbindlich</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ------------- Email-Link verschickt ------------- */}
          {emailSent && (
            <div className="Anmelden-container">
              <div className="Anmelden-email-sent">
                <h2>Wir haben dir einen Link zur Anmeldung geschickt</h2>
                <p>
                  Prüfe dein Email-Postfach unter {email} auf eine Nachricht von
                  Autoeinfachlos.
                </p>
                {getMailInfo(email) && (
                  <div className="Email-sent-button-wrapper">
                    <p>Gehe zu</p>
                    <a
                      className="Email-sent-button"
                      href={getMailInfo(email).link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Image
                        src={getMailInfo(email).image}
                        alt="email provider"
                        className="Email-Image"
                      />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AnmeldenSection;
